import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Modal, Button, Message, Icon, Loader, Dimmer } from "semantic-ui-react";

// Models
import PaymentModel, { AccountStatus } from "../../../../../models/payment";
import I18n from "../../../../../i18n";

export default function VerifyStripeAccount() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);

    const verifyStripeAccount = useCallback(async () => {
        try {
            setLoading(true);
            const status = await PaymentModel.verifyStripeAccount();

            // Guardamos el estado recibido (incluyendo 'ACTIVE') para renderizar el mensaje correspondiente
            setAccountStatus(status);

            setLoading(false);
        } catch (error) {
            console.error(I18n.t('verifyAccount.error.logMessage'), error);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Capturamos el parámetro que configuramos previamente (?onboarding=success o stripe)
        const onboarding = searchParams.get("onboarding");

        if (onboarding === "stripe" || onboarding === "success") {
            setOpen(true);
            verifyStripeAccount();
        }
    }, [searchParams, verifyStripeAccount]);

    // Redirección manual hacia la sección de cuenta una vez que el usuario confirma el éxito
    const handleGoToAccount = () => {
        setOpen(false);
        setAccountStatus(null);
        navigate("/setup/account");
    };

    // Manejador del CTA cuando la cuenta está INCOMPLETE o requiere volver a Stripe
    const handleReconnectStripe = async () => {
        try {
            setActionLoading(true);
            const onboardingUrl = await PaymentModel.stripeConnectOnboarding();

            if (onboardingUrl) {
                const newWindow = window.open('', '_blank');

                if (newWindow) {
                    newWindow.document.write(`<h3>${I18n.t('setup.publishProfileTexts.connectingStripe')}</h3>`);
                    newWindow.document.title = I18n.t('messages.loading');
                    newWindow.location.href = onboardingUrl;
                }
            } else {
                setActionLoading(false);
            }
        } catch (error) {
            console.error(I18n.t('verifyAccount.error.redirectError'), error);
            setActionLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setAccountStatus(null);
        // Limpiamos la URL al cerrar el modal para evitar re-ejecuciones molestas al recargar
        searchParams.delete("onboarding");
        setSearchParams(searchParams);
    };

    // Renderizado dinámico del mensaje interno del modal basado en el status
    const renderModalBody = () => {
        if (loading) {
            return (
                <div style={{ minHeight: "150px", position: "relative" }}>
                    <Dimmer active inverted>
                        <Loader inverted>{I18n.t('verifyAccount.loading')}</Loader>
                    </Dimmer>
                </div>
            );
        }

        switch (accountStatus) {
            case "ACTIVE":
                return (
                    <Message success icon style={{ margin: 0 }}>
                        <Icon name="check circle" />
                        <Message.Content>
                            <Message.Header>{I18n.t('verifyAccount.active.title')}</Message.Header>
                            <p>{I18n.t('verifyAccount.active.message')}</p>
                        </Message.Content>
                    </Message>
                );

            case "PENDING_VERIFICATION":
                return (
                    <Message info icon style={{ margin: 0 }}>
                        <Icon name="clock outline" loading />
                        <Message.Content>
                            <Message.Header>{I18n.t('verifyAccount.pending.title')}</Message.Header>
                            <p>{I18n.t('verifyAccount.pending.message1')}</p>
                            <p><strong>{I18n.t('verifyAccount.pending.message2')}</strong></p>
                        </Message.Content>
                    </Message>
                );

            case "INCOMPLETE":
                return (
                    <Message warning icon style={{ margin: 0 }}>
                        <Icon name="exclamation triangle" />
                        <Message.Content>
                            <Message.Header>{I18n.t('verifyAccount.incomplete.title')}</Message.Header>
                            <p>{I18n.t('verifyAccount.incomplete.message1')}</p>
                            <p>{I18n.t('verifyAccount.incomplete.message2')}</p>
                        </Message.Content>
                    </Message>
                );

            default:
                return (
                    <Message negative icon style={{ margin: 0 }}>
                        <Icon name="warning sign" />
                        <Message.Content>
                            <Message.Header>{I18n.t('verifyAccount.error.title')}</Message.Header>
                            <p>{I18n.t('verifyAccount.error.message')}</p>
                        </Message.Content>
                    </Message>
                );
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            size="small"
            closeOnDimmerClick={!loading}
        >
            <Modal.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                    <Icon name="stripe" color="violet" /> {I18n.t('verifyAccount.modalHeader')}
                </span>
            </Modal.Header>

            <Modal.Content>
                {renderModalBody()}
            </Modal.Content>

            <Modal.Actions>
                {accountStatus !== "ACTIVE" && (
                    <Button
                        disabled={loading || actionLoading}
                        onClick={handleClose}
                        basic
                    >
                        {I18n.t('verifyAccount.closeButton')}
                    </Button>
                )}

                {accountStatus === "ACTIVE" && (
                    <Button
                        success
                        color="green"
                        onClick={handleGoToAccount}
                        content={I18n.t('verifyAccount.active.ctaButton')}
                        icon="arrow right"
                        labelPosition="right"
                    />
                )}

                {accountStatus === "INCOMPLETE" && (
                    <Button
                        primary
                        loading={actionLoading}
                        disabled={loading}
                        onClick={handleReconnectStripe}
                        content={I18n.t('verifyAccount.incomplete.ctaButton')}
                        icon="arrow right"
                        labelPosition="right"
                        style={{ backgroundColor: "#6435c9" }}
                    />
                )}

                {accountStatus === "PENDING_VERIFICATION" && (
                    <Button
                        color="green"
                        loading={loading}
                        disabled={loading}
                        onClick={verifyStripeAccount}
                        content={I18n.t('verifyAccount.pending.checkButton')}
                        icon="refresh"
                    />
                )}
            </Modal.Actions>
        </Modal>
    );
}